import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpSentEvent,
  HttpHeaderResponse,
  HttpProgressEvent,
  HttpResponse,
  HttpUserEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  Observable,
  switchMap,
  take,
  tap,
  throwError as observableThrowError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { SignInResponse } from '../models/sign-in-response';

@Injectable({
  providedIn: 'root',
})
export class Interceptor implements HttpInterceptor {
  isRefreshingToken = false;
  tokenSubject = new BehaviorSubject<string | undefined>(undefined);

  constructor(private authService: AuthService, private router: Router) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<
    | HttpSentEvent
    | HttpHeaderResponse
    | HttpProgressEvent
    | HttpResponse<any>
    | HttpUserEvent<any>
    | any
  > {
    const authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
    const credentials = JSON.parse(
      localStorage.getItem(authLocalStorageToken)!
    ) as SignInResponse;
    request = this.addTokenInHeader(request, credentials?.accessToken);
    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
        }
      }),
      catchError((error) => {
        if (error instanceof HttpErrorResponse) {
          switch ((error as HttpErrorResponse).status) {
            case 401:
              return this.handle401Error(request, next);
            default:
              return next.handle(
                this.addTokenInHeader(request, credentials?.accessToken)
              );
          }
        } else {
          return new Error(error);
        }
      }),
      finalize(() => { })
    );
  }

  addTokenInHeader(request: HttpRequest<any>, accessToken: string) {
    if (accessToken && (request.url.startsWith(environment.api) || request.url.startsWith(environment.authApi))) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken.replace(/['"]+/g, '')}`,
        },
      });
    }
    return request;
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler): any {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;
      this.tokenSubject.next(undefined);
      return this.authService.refreshToken().pipe(
        switchMap((res) => {
          if (res) {
            this.tokenSubject.next(res.accessToken);
            localStorage.setItem('accessToken', res.accessToken);
            localStorage.setItem('expiresIn', String(res.expiresIn));
            localStorage.setItem('refreshToken', res.refreshToken);

            return next.handle(this.addTokenInHeader(req, res.accessToken));
          }
          return this.router.navigateByUrl('/');
        }),
        catchError(() => {
          return this.router.navigateByUrl('/');
        }),
        finalize(() => {
          this.isRefreshingToken = false;
        })
      );
    } else {
      return this.tokenSubject.pipe(
        filter((token) => token !== undefined),
        take(1),
        switchMap((token) => {
          return next.handle(this.addTokenInHeader(req, token!));
        })
      );
    }
  }
}
